import { memo, useCallback } from 'react'

import { Box, TextField } from '@mui/material'
import { ExpAppInsert } from '@nativewaves/nw-cloud-sdk/exp'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { SubmissionProgress } from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useAppCreateMutation } from 'hooks/mutations/exp'
import { expAppsRoute } from 'pages/NWPlatform/ExpApps'

type FormStructure = ExpAppInsert

type NewDialogProps = {
  onClose?: () => void
}

const NewDialogRaw = ({ onClose }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { environmentId } = expAppsRoute.useParams()

  const appCreateMutation = useAppCreateMutation()

  const { handleSubmit, formState, control } = useForm<FormStructure>({
    defaultValues: {
      environmentId,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      appCreateMutation.mutate(
        { expAppInsert: data },
        {
          onSuccess: () => {
            onClose?.()
          },
        },
      )
    },
    [appCreateMutation, onClose],
  )

  return (
    <DialogFormContent
      title={t('domain:ExpApp.createExpApp')}
      description={t('domain:ExpApp.expAppDescription')}
      onSubmit={handleSubmit(handleFormSubmit)}
      isInitialLoading={appCreateMutation.isPending}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={appCreateMutation.isSuccess} isError={appCreateMutation.isError} />
      }
      SaveButtonProps={{
        disabled: (formState.isSubmitted && !formState.isValid) || appCreateMutation.isSuccess,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              autoFocus
              margin="dense"
              label={t('name')}
              type="text"
              fullWidth
              variant="standard"
              required
              autoComplete="off"
              slotProps={{
                input: {
                  endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />,
                },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="config.urlConfig.cdnUrl"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              autoFocus
              margin="dense"
              label={t('cdnUrl')}
              type="text"
              fullWidth
              variant="standard"
              required
              autoComplete="off"
              slotProps={{
                input: {
                  endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />,
                },
              }}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
